import { useContext, useEffect, useMemo, useState } from 'react';
import {
  ChevronIcon,
  CrossIcon,
  LightBulbIcon,
  PlusIcon,
  SearchIcon,
} from 'assets/icons';
import { Button, ButtonSize, ButtonStyle } from 'common/button';
import { Checkbox } from 'common/checkbox';
import { NotificationType } from 'common/enums';
import { Modal } from 'common/modal';
import { appActions, AppContext } from 'context/app-context';
import { classnames } from 'helpers/utils';
import { NotificationObject } from 'models/notificationObject';
import { UserController } from 'networking/controllers/user-controller';
import { VaultsController } from 'networking/controllers/vaults-controller';
import { goToPage, RouteName } from 'routes';
import styles from './suggested-prompts-modal.module.scss';

// Where prompts come from: the pre-built topic catalog, the user's own
// interview templates (managed on the Templates screen), or typed in here.
type Source = 'topics' | 'templates' | 'custom';

const SOURCE_TABS: { key: Source; label: string }[] = [
  { key: 'topics', label: 'Topics' },
  { key: 'templates', label: 'My Templates' },
  { key: 'custom', label: 'Custom' },
];

interface SuggestedPromptsModalProps {
  vaultId: number;
  // The album the prompts go to. Omit it and pass `albums` to let the user
  // pick one (e.g. from the vault-level New menu).
  albumId?: number;
  albums?: { id: number; name: string }[];
  // Names of prompts already in the album, so they can't be added twice.
  existingPromptNames?: string[];
  onClose: () => void;
  // Called after at least one prompt was created.
  onAdded: () => void;
}

// Topic and template ids live in different namespaces, so the source is part
// of the key. The question text is last so it can be recovered on submit.
const selectionKey = (source: Source, categoryId: string, question: string) =>
  `${source}::${categoryId}::${question}`;
const questionFromKey = (key: string) => key.split('::').slice(2).join('::');

const normalize = (text: string) => text.trim().toLowerCase();

// Adds prompts to the album — video entries with no recording yet. Questions
// can be picked from the pre-defined topic catalog (the API's suggested
// questions: 15 categories such as Heritage or Growing Up and School), from
// the user's interview templates, or typed in as custom prompts. Picks from
// all three tabs are added together.
export const SuggestedPromptsModal = ({
  vaultId,
  albumId,
  albums,
  existingPromptNames = [],
  onClose,
  onAdded,
}: SuggestedPromptsModalProps) => {
  const { dispatch } = useContext(AppContext);
  const [source, setSource] = useState<Source>('topics');
  // 0 means "no album": the prompt is filed in the vault's default album and
  // shows up in the dashboard's Prompts list.
  const [targetAlbumId, setTargetAlbumId] = useState(albumId ?? 0);
  const [categories, setCategories] = useState<PreBuiltQuestionsType[]>([]);
  const [templates, setTemplates] = useState<PreBuiltQuestionsType[]>([]);
  const [loading, setLoading] = useState(true);
  // Only the two fetched sources can fail to load.
  const [failed, setFailed] = useState<Record<'topics' | 'templates', boolean>>(
    { topics: false, templates: false },
  );
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Custom tab: the question being typed, and the ones queued so far.
  const [customDraft, setCustomDraft] = useState('');
  const [customPrompts, setCustomPrompts] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  // Category ids whose image failed to load; they get the fallback icon.
  const [brokenImages, setBrokenImages] = useState<Set<string>>(new Set());

  // Both sources load up front so switching tabs is instant.
  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([
      VaultsController.getDefaultQuestions(),
      UserController.getCustomTemplates(),
    ]).then(([topicsResult, templatesResult]) => {
      if (cancelled) return;
      if (topicsResult.status === 'fulfilled') {
        const { suggestedQuestions } = topicsResult.value;
        setCategories(suggestedQuestions);
        setExpanded(suggestedQuestions[0]?.id ?? null);
      } else {
        setFailed((prev) => ({ ...prev, topics: true }));
      }
      if (templatesResult.status === 'fulfilled') {
        setTemplates(templatesResult.value.templates);
      } else {
        setFailed((prev) => ({ ...prev, templates: true }));
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const listFor = (which: Source) => {
    if (which === 'topics') return categories;
    if (which === 'templates') return templates;
    return [];
  };
  const sourceList = listFor(source);
  const sourceFailed = source !== 'custom' && failed[source];

  const handleSourceChange = (next: Source) => {
    if (next === source) return;
    setSource(next);
    setQuery('');
    setExpanded(listFor(next)[0]?.id ?? null);
  };

  const existing = useMemo(
    () => new Set(existingPromptNames.map(normalize)),
    [existingPromptNames],
  );
  const alreadyAdded = (question: string) => existing.has(normalize(question));

  // Queue the typed question. Blank, duplicate, and already-present prompts
  // are ignored.
  const addCustomPrompt = () => {
    const name = customDraft.trim();
    if (!name) return;
    const dup =
      customPrompts.some((p) => normalize(p) === normalize(name)) ||
      existing.has(normalize(name));
    if (!dup) setCustomPrompts((prev) => [...prev, name]);
    setCustomDraft('');
  };

  const removeCustomPrompt = (index: number) =>
    setCustomPrompts((prev) => prev.filter((_, i) => i !== index));

  // Filter by question text; a category also matches on its own name.
  const visibleCategories = useMemo(() => {
    const needle = normalize(query);
    if (!needle) return sourceList;
    return sourceList
      .map((category) => {
        if (normalize(category.name).includes(needle)) return category;
        const questions = category.questions.filter((q) =>
          normalize(q.name).includes(needle),
        );
        return questions.length ? { ...category, questions } : null;
      })
      .filter((c): c is PreBuiltQuestionsType => c !== null);
  }, [sourceList, query]);

  const toggleQuestion = (categoryId: string, question: string) => {
    const key = selectionKey(source, categoryId, question);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Select / clear every addable question in a category.
  const toggleCategory = (category: PreBuiltQuestionsType) => {
    const addable = category.questions
      .map((q) => q.name)
      .filter((name) => !alreadyAdded(name));
    const keys = addable.map((name) => selectionKey(source, category.id, name));
    const allSelected = keys.every((key) => selected.has(key));
    setSelected((prev) => {
      const next = new Set(prev);
      keys.forEach((key) => (allSelected ? next.delete(key) : next.add(key)));
      return next;
    });
  };

  // A typed-but-not-queued question still counts, so the footer button works
  // without pressing Add first.
  const pendingDraft = customDraft.trim();
  const selectedCount =
    selected.size + customPrompts.length + (pendingDraft ? 1 : 0);
  const addLabel = (() => {
    if (saving) return 'Adding…';
    if (!selectedCount) return 'Add prompts';
    return `Add ${selectedCount} ${selectedCount === 1 ? 'prompt' : 'prompts'}`;
  })();

  const notify = (title: string, type: NotificationType, message?: string) =>
    dispatch({
      type: appActions.NOTIFICATION,
      notification: new NotificationObject({
        show: true,
        title,
        type,
        message,
      }),
    });

  const handleAdd = async () => {
    if (!selectedCount || saving) return;
    setSaving(true);
    // The same question may be picked from a topic and a template; add once.
    const names = Array.from(
      new Set([
        ...Array.from(selected).map(questionFromKey),
        ...customPrompts,
        ...(pendingDraft ? [pendingDraft] : []),
      ]),
    );
    const results = await Promise.allSettled(
      names.map((name) =>
        VaultsController.createClip(vaultId, {
          clip: { name, albumId: targetAlbumId },
        }),
      ),
    );
    const failedCount = results.filter((r) => r.status === 'rejected').length;
    const added = names.length - failedCount;
    setSaving(false);

    if (added > 0) onAdded();
    if (failedCount === 0) {
      notify(
        added === 1 ? 'Prompt added' : `${added} prompts added`,
        NotificationType.Success,
      );
      onClose();
    } else {
      notify(
        'Some prompts could not be added',
        NotificationType.Error,
        `${added} added, ${failedCount} failed. Please try again.`,
      );
    }
  };

  const showCategory = (category: PreBuiltQuestionsType) => {
    const isOpen = expanded === category.id || !!query;
    const addable = category.questions.filter((q) => !alreadyAdded(q.name));
    const pickedHere = addable.filter((q) =>
      selected.has(selectionKey(source, category.id, q.name)),
    ).length;
    const allPicked = addable.length > 0 && pickedHere === addable.length;

    return (
      <section key={category.id} className={styles.category}>
        <div className={styles.categoryHeader}>
          <button
            type="button"
            className={styles.categoryToggle}
            aria-expanded={isOpen}
            onClick={() => setExpanded(isOpen && !query ? null : category.id)}
          >
            {category.imageUrl && !brokenImages.has(category.id) ? (
              <img
                className={styles.categoryImage}
                src={category.imageUrl}
                alt=""
                loading="lazy"
                onError={() =>
                  setBrokenImages((prev) => new Set(prev).add(category.id))
                }
              />
            ) : (
              <span className={styles.categoryImageFallback} aria-hidden="true">
                <LightBulbIcon />
              </span>
            )}
            <span className={styles.categoryText}>
              <span className={styles.categoryName}>{category.name}</span>
              <span className={styles.categoryMeta}>
                {category.questions.length} prompts
                {pickedHere > 0 && ` · ${pickedHere} selected`}
              </span>
            </span>
            <ChevronIcon
              className={classnames(styles.chevron, {
                [styles.chevronOpen]: isOpen,
              })}
            />
          </button>
          {addable.length > 0 && (
            <button
              type="button"
              className={styles.selectAll}
              onClick={() => toggleCategory(category)}
            >
              {allPicked ? 'Clear' : 'Select all'}
            </button>
          )}
        </div>
        {isOpen && (
          <ul className={styles.questions}>
            {category.questions.map((question) => {
              const added = alreadyAdded(question.name);
              const key = selectionKey(source, category.id, question.name);
              const checked = added || selected.has(key);
              return (
                <li
                  key={key}
                  className={classnames(styles.question, {
                    [styles.questionAdded]: added,
                  })}
                >
                  <Checkbox
                    itemKey={key}
                    isChecked={checked}
                    disabled={added}
                    onChange={() => toggleQuestion(category.id, question.name)}
                  />
                  <button
                    type="button"
                    className={styles.questionText}
                    disabled={added}
                    onClick={() => toggleQuestion(category.id, question.name)}
                  >
                    {question.name}
                  </button>
                  {added && <span className={styles.addedTag}>Added</span>}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    );
  };

  return (
    <Modal
      title="Add prompts"
      onClose={onClose}
      contentClassName={styles.modal}
    >
      <div className={styles.body}>
        <p className={styles.intro}>
          Pick questions from our pre-built topics or your own interview
          templates. Each one becomes a prompt in{' '}
          {albums ? 'the album you pick (or none)' : 'this album'}, ready to
          record.
        </p>
        {albums && (
          <div className={styles.albumField}>
            <span className={styles.albumLabel} aria-hidden="true">
              Album
            </span>
            <select
              className={styles.albumSelect}
              aria-label="Album"
              value={targetAlbumId}
              onChange={(e) => setTargetAlbumId(Number(e.target.value))}
            >
              <option value={0}>No album</option>
              {albums.map((album) => (
                <option key={album.id} value={album.id}>
                  {album.name || 'Untitled'}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className={styles.tabs} role="tablist">
          {SOURCE_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={source === tab.key}
              className={classnames(styles.tab, {
                [styles.tabActive]: source === tab.key,
              })}
              onClick={() => handleSourceChange(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {source !== 'custom' && (
          <div className={styles.search}>
            <SearchIcon className={styles.searchIcon} />
            <input
              type="search"
              className={styles.searchInput}
              placeholder={
                source === 'topics'
                  ? 'Search topics and questions'
                  : 'Search templates and questions'
              }
              aria-label="Search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        )}

        {source === 'custom' && (
          <div className={styles.customEntry}>
            <input
              type="text"
              className={styles.customInput}
              placeholder="Type a question, e.g. What was your first job?"
              aria-label="Custom prompt"
              value={customDraft}
              onChange={(e) => setCustomDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addCustomPrompt();
                }
              }}
            />
            <button
              type="button"
              className={styles.customAdd}
              disabled={!pendingDraft}
              onClick={addCustomPrompt}
            >
              <PlusIcon />
              <span>Add another</span>
            </button>
          </div>
        )}

        <div className={styles.list}>
          {source === 'custom' && customPrompts.length === 0 && (
            <p className={styles.message}>
              Write your own question above. Press Enter to queue it and write
              another, or just click Add below.
            </p>
          )}
          {source === 'custom' &&
            customPrompts.map((prompt, index) => (
              // Queued prompts are de-duplicated, so the text is a stable key.
              <div key={prompt} className={styles.customRow}>
                <span className={styles.customRowIcon}>
                  <LightBulbIcon />
                </span>
                <span className={styles.customRowText}>{prompt}</span>
                <button
                  type="button"
                  className={styles.customRemove}
                  aria-label="Remove prompt"
                  onClick={() => removeCustomPrompt(index)}
                >
                  <CrossIcon />
                </button>
              </div>
            ))}
          {source !== 'custom' && loading && (
            <p className={styles.message}>Loading…</p>
          )}
          {!loading && sourceFailed && (
            <p className={styles.message}>
              {source === 'topics' ? 'Topics' : 'Your templates'} couldn&apos;t
              be loaded right now. Please try again in a moment.
            </p>
          )}
          {!loading &&
            !sourceFailed &&
            source === 'templates' &&
            templates.length === 0 && (
              <div className={styles.emptyTemplates}>
                <p className={styles.message}>
                  You haven&apos;t created any interview templates yet. Build a
                  reusable list of questions once, then add it to any album.
                </p>
                <Button
                  buttonStyle={ButtonStyle.SecondaryGray}
                  buttonSize={ButtonSize.Medium}
                  onClick={() => goToPage(RouteName.Templates)}
                >
                  Create a template
                </Button>
              </div>
            )}
          {source !== 'custom' &&
            !loading &&
            !sourceFailed &&
            sourceList.length > 0 &&
            visibleCategories.length === 0 && (
              <p className={styles.message}>
                Nothing found matching &quot;{query}&quot;
              </p>
            )}
          {visibleCategories.map(showCategory)}
        </div>

        <div className={styles.footer}>
          <Button
            buttonStyle={ButtonStyle.SecondaryGray}
            buttonSize={ButtonSize.Medium}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            buttonStyle={ButtonStyle.Primary}
            buttonSize={ButtonSize.Medium}
            disabled={!selectedCount || saving}
            onClick={handleAdd}
          >
            {addLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
